// src/pages/UserManagement/modals/AvatarUploadModal.tsx
// Avatar upload modal with image preview

import React, { useState, useRef } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Progress,
  Alert,
} from 'reactstrap';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { uploadAvatar } from 'slices/thunks';
import { User } from '../types';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('user_management');

interface AvatarUploadModalProps {
  isOpen: boolean;
  toggle: () => void;
  user: User | null;
}

const AvatarUploadModal: React.FC<AvatarUploadModalProps> = ({
  isOpen,
  toggle,
  user,
}) => {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string>('');
  
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        setError(st('chi_chap_nhan_file_anh_jpg_text'));
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError(st('kich_thuoc_file_khong_duoc_vuot_text'));
        return;
      }
      
      setError('');
      setSelectedFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  
    const handleUpload = async () => {
    if (!selectedFile || !user) return;
    
    setUploading(true);
    setUploadProgress(0);
    setError('');
    
    try {
      // Simulate upload progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);
      
      // Upload avatar using Redux thunk
      const resultAction = await dispatch(uploadAvatar({ id: user.id, file: selectedFile }));
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (uploadAvatar.fulfilled.match(resultAction)) {
        // Success
        setTimeout(() => {
          handleClose();
        }, 500);
      } else {
        // Handle rejection
        throw new Error(resultAction.payload as string || st('co_loi_xay_ra_khi_tai_message'));
      }
    } catch (err: any) {
      setError(err?.message || st('co_loi_xay_ra_khi_tai_text'));
      setUploading(false);
      setUploadProgress(0);
    }
  };
  
    const handleClose = () => {
    if (!uploading) {
      setSelectedFile(null);
      setPreviewUrl('');
      setUploading(false);
      setUploadProgress(0);
      setError('');
      toggle();
    }
  };
  
  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };
  
  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered>
            <ModalHeader toggle={handleClose}>
        <i className="ri-camera-line me-2"></i>
        {st('doi_anh_dai_dien_title')} {user?.name}
      </ModalHeader>
      <ModalBody>
        {/* Error Alert */}
        {error && (
          <Alert color="danger" className="mb-3">
            <i className="ri-error-warning-line me-2"></i>
            {error}
          </Alert>
        )}
        
        <div className="text-center">
          {/* Current Avatar or Preview */}
          <div className="mb-3">
            <img
              src={previewUrl || user?.avatar || '/icon.jpg'}
              alt={st('avatar_preview_alt')}
              className="rounded-circle"
              style={{
                width: '150px',
                height: '150px',
                objectFit: 'cover',
                border: '3px solid var(--vz-primary)',
              }}
            />
          </div>
          
          {/* File Input (Hidden) */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            data-action-code="input-avatar-file"
            data-action-name="Ảnh đại diện"
            onChange={handleFileSelect}
            disabled={!PageActionPermission.canEnable(location.pathname, "input-avatar-file")}
            style={{ display: 'none' }}
          />

                    {/* Choose File Button */}
          {!selectedFile && PageActionPermission.canDisplay(location.pathname, "button-choose-file") && (
            <Button
              color="primary"
              data-action-code="button-choose-file"
              data-action-name="Chọn ảnh đại diện"
              onClick={handleChooseFile}
              disabled={uploading}
            >
              <i className="ri-image-add-line me-1"></i>
              {st('chon_anh_button')}
            </Button>
          )}
          
                    {/* File Info */}
          {selectedFile && (
            <div className="mt-3">
              <div className="alert alert-info mb-2">
                <strong>{selectedFile.name}</strong>
                <br />
                <small>{(selectedFile.size / 1024).toFixed(2)} {st('kb_hint')}</small>
              </div>
              
              {!uploading && PageActionPermission.canDisplay(location.pathname, "button-remove-selected-avatar") && (
                <Button
                  color="light"
                  size="sm"
                  data-action-code="button-remove-selected-avatar"
                  data-action-name="Bỏ chọn ảnh đại diện"
                  onClick={() => {
                    setSelectedFile(null);
                    setPreviewUrl('');
                    setError('');
                  }}
                >
                  <i className="ri-close-line me-1"></i>
                  {st('xoa_button')}
                </Button>
              )}
            </div>
          )}
          
                    {/* Upload Progress */}
          {uploading && (
            <div className="mt-3">
              <div className="d-flex justify-content-between mb-1">
                <small className="text-muted">{st('dang_tai_len_hint')}</small>
                <small className="text-muted">{uploadProgress}%</small>
              </div>
              <Progress value={uploadProgress} color="primary" />
            </div>
          )}
          
                    {/* Guidelines */}
          <div className="mt-3 text-start">
            <small className="text-muted">
              <strong>{st('huong_dan_text')}</strong>
              <ul className="mb-0">
                <li>{st('kich_thuoc_toi_da_5mb_text')}</li>
                <li>{st('dinh_dang_ho_tro_jpg_png_text')}</li>
                <li>{st('kich_thuoc_khuyen_nghi_400x400_pixels_text')}</li>
              </ul>
            </small>
          </div>
        </div>
      </ModalBody>
            <ModalFooter>
        <Button color="light" onClick={handleClose} disabled={uploading}>
          <i className="ri-close-line me-1"></i>
          {st('huy_button')}
        </Button>
        {PageActionPermission.canDisplay(location.pathname, "button-upload-submit") && (
          <Button
            color="primary"
            data-action-code="button-upload-submit"
            data-action-name="Lưu ảnh đại diện"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
          >
            {uploading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1"></span>
                {st('dang_tai_len_hint')}
              </>
            ) : (
              <>
                <i className="ri-upload-2-line me-1"></i>
                {st('luu_anh_text')}
              </>
            )}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default AvatarUploadModal;